/**
 * EventCache — chunk-based event caching layer backed by localForage.
 *
 * Design:
 *   Each 15-minute time window is cached independently as a "chunk", matching
 *   the API's natural request granularity (eventsApi.requestIncrement = 15 min).
 *   Chunks are keyed as "{deviceId}.chunk.{chunkStartTimestamp}".
 *
 *   This avoids the problems of contiguous-range caching (gap-filling bugs,
 *   excessive API calls on custom date ranges). Any time range is simply the
 *   union of its constituent 15-min chunks.
 *
 * Fetching:
 *   getEventsForIdentity() enumerates chunks for the requested range, checks
 *   the cache for each, and batch-fetches uncached/expired chunks (3 concurrent).
 *   Results are filtered to the caller's exact range and deduplicated by key.
 *   Multiple dialogs (EventDialog, WebsiteDialog, ScreenTimeDialog) share the
 *   same cache — chunks loaded by one dialog are reused by others.
 *
 * TTL (dynamic, determined at fetch time based on chunk age):
 *   Events expire server-side after 15 days, so older chunks change less
 *   frequently and can be cached longer. The TTL is computed once when the
 *   chunk is fetched from the API and stored with the cache entry:
 *
 *     Chunk age at fetch time    →  TTL
 *     ─────────────────────────────────────
 *     < 1 hour                   →  not cached (always fresh)
 *     1–6 hours                  →  30 minutes
 *     6–12 hours                 →  1 hour
 *     12–24 hours                →  1 day
 *     24+ hours                  →  3 days
 *
 *   The TTL is capped so the cache entry never extends past chunkStart + 15 days
 *   (the server-side event retention limit).
 *
 * Consumers: EventDialog, WebsiteDialog, ScreenTimeDialog, Timeline
 * Clear cache: Contact.vue "Clear Event Cache" button calls clearCache()
 */
import eventsApi, { requestIncrement } from "../aws/events/eventsApi";
import localForage from "localforage";
import AWSClockSkewHandler from "../aws/AWSClockSkewHandler";
import E2EEHelperCache from "./e2ee/E2EEHelperCache";
import E2EEPasscodePrompt from "./e2ee/E2EEPasscodePrompt";
import { Nc1UnlockPrompt } from "./e2ee/nc1/Unlock";
import { classifyE2EEError, E2EE_ERROR_CODES } from "./e2ee/E2EEErrors";

const store = localForage.createInstance({
  storeName: 'eventCache'
})

const CHUNK_SIZE_MS = requestIncrement * 60 * 1000 // 15 min, matches API request size
const MAX_EVENT_AGE_MS = 15 * 24 * 60 * 60 * 1000  // events expire server-side after 15 days

function alignChunkStart(timestamp) {
  return Math.floor(timestamp / CHUNK_SIZE_MS) * CHUNK_SIZE_MS
}

function createChunkKey(identity, chunkStart) {
  return `${identity}.chunk.${chunkStart}`
}

function isRecentChunk(chunkStart) {
  // Chunks less than 1 hour old get TTL of 0 — never cache them
  let age = AWSClockSkewHandler.getTime() - chunkStart
  return age < 60 * 60 * 1000
}

// Compute TTL based on how old the chunk was when fetched from the API.
// Older events are less likely to change, so they get longer TTLs.
// The TTL is stored with the cache entry so it doesn't shift over time.
function computeTTL(chunkStart, fetchedAt) {
  let age = fetchedAt - chunkStart
  let ttl
  if (age >= 24 * 60 * 60 * 1000) ttl = 3 * 24 * 60 * 60 * 1000  // 24h+ old → 3 day TTL
  else if (age >= 12 * 60 * 60 * 1000) ttl = 24 * 60 * 60 * 1000  // 12h+ old → 1 day TTL
  else if (age >= 6 * 60 * 60 * 1000) ttl = 60 * 60 * 1000        // 6h+ old  → 1 hour TTL
  else if (age >= 1 * 60 * 60 * 1000) ttl = 30 * 60 * 1000        // 1h+ old  → 30 min TTL
  else return 0                                                      // < 1h old → no caching

  // Cap so the cache entry never lives past the 15-day event retention limit
  let maxTTL = chunkStart + MAX_EVENT_AGE_MS - fetchedAt
  return Math.max(0, Math.min(ttl, maxTTL))
}

function isChunkExpired(entry) {
  return AWSClockSkewHandler.getTime() - entry.created > entry.ttl
}

// True if a cached chunk contains any event that failed to decrypt. Such chunks
// must not be served from cache (re-fetch instead) so the data appears once the
// user can decrypt it. Also self-heals chunks persisted before we stopped
// caching decrypt failures.
function chunkHasDecryptFailure(entry) {
  if (!entry || !entry.events) {
    return false
  }
  for (let event of entry.events) {
    if (event.encrypted && event.decrypted === false) {
      return true
    }
  }
  return false
}

function enrichEvents(events) {
  for (let event of events) {
    event.timestamp = parseInt(event.timestamp)
    event.timestampString = new Date(event.timestamp).toLocaleString()
    event.key = `${event.timestamp}.${event.eventType}.${event.riskRating}.${event.eventTitle}.${event.eventBody}`
  }
  return events
}

// Returns true only if every encrypted event decrypted successfully. Callers
// use this to decide whether the chunk is safe to persist — a chunk with any
// failure must not be cached (otherwise the "failed to decrypt" placeholders
// would be served even after the user enters their passcode).
async function decryptEvents(accountId, identityId, events) {
  let e2eeHelper = null
  let allDecrypted = true

  for (let event of events) {
    if (event.encrypted) {

      if (e2eeHelper === null) {
        console.log('getting e2ee helper')
        e2eeHelper = E2EEHelperCache.getE2EEHelper(accountId)
        console.log('got it')
      }

      try {
        event.eventTitle = await e2eeHelper.decryptString(identityId, event.eventTitle, event.timestamp)
        event.eventBody = await e2eeHelper.decryptString(identityId, event.eventBody, event.timestamp)
        event.appName = await e2eeHelper.decryptString(identityId, event.appName, event.timestamp)
        event.packageName = await e2eeHelper.decryptString(identityId, event.packageName, event.timestamp)
        event.decrypted = true
      } catch (e) {
        event.packageName = 'unknown (failed to decrypt)'
        event.appName = 'unknown (failed to decrypt)'
        event.decrypted = false
        event.decryptionError = e
        allDecrypted = false
        // If this is a missing-passcode failure, ask the global dialog to
        // prompt. The controller de-dupes per account, so a chunk full of
        // encrypted events won't fire dozens of prompts.
        const classified = classifyE2EEError(e)
        if(classified.code === E2EE_ERROR_CODES.PASSCODE_REQUIRED) {
          E2EEPasscodePrompt.request(accountId)
        } else if(classified.code === E2EE_ERROR_CODES.NC1_UNLOCK_REQUIRED) {
          // NC1: the error names the identity to unlock — a guest's OWN one
          // when viewing someone else's account (as the thumbnails already
          // do; before 2026-09-30 this path asked a guest for the owner's
          // recovery code).
          Nc1UnlockPrompt.request(classified.accountId || accountId)
        }
      }
    }
  }

  console.log('decryptEvents done')
  return allDecrypted
}

class EventCache {

  constructor() {
    this.cleanUp()
  }

  async getEventsForIdentity(accountId, identity, current, until, /* totalEvents - accepted but unused */) {
    // 1. Compute chunk range
    let firstChunkStart = alignChunkStart(until)
    let lastChunkStart = alignChunkStart(current)

    let allChunkStarts = []
    for (let cs = firstChunkStart; cs <= lastChunkStart; cs += CHUNK_SIZE_MS) {
      allChunkStarts.push(cs)
    }

    // 2. Check cache for each chunk
    let cachedChunks = {}  // chunkStart -> events array
    let uncachedStarts = []

    for (let cs of allChunkStarts) {
      if (isRecentChunk(cs)) {
        uncachedStarts.push(cs)
        continue
      }

      let key = createChunkKey(identity, cs)
      let entry = await store.getItem(key)
      if (entry && entry.ttl == null) {
        entry.ttl = computeTTL(cs, entry.created)
      }

      if (entry && !isChunkExpired(entry) && !chunkHasDecryptFailure(entry)) {
        cachedChunks[cs] = entry.events
      } else {
        uncachedStarts.push(cs)
      }
    }

    // 3. Batch-fetch uncached chunks (3 concurrent)
    const maxConcurrent = 3
    for (let i = 0; i < uncachedStarts.length; i += maxConcurrent) {
      let batch = []
      for (let b = 0; b < maxConcurrent && i + b < uncachedStarts.length; b++) {
        let cs = uncachedStarts[i + b]
        let chunkEnd = cs + CHUNK_SIZE_MS
        batch.push(
          eventsApi.getEvents(accountId, identity, chunkEnd, cs, 'hours')
            .then(result => {
              let events = (result && result.data && result.data.events) ? result.data.events : []
              enrichEvents(events)
              return { chunkStart: cs, events }
            })
            .catch(err => {
              console.log('Error fetching chunk', cs, err)
              return { chunkStart: cs, events: [] }
            })
        )
      }

      let batchResults = await Promise.all(batch)

      for (let { chunkStart, events } of batchResults) {
        let allDecrypted = await decryptEvents(accountId, identity, events)
        cachedChunks[chunkStart] = events

        // Cache non-recent chunks to localForage — but never persist a chunk
        // that had a decryption failure, so it gets re-fetched and re-decrypted
        // later (e.g. once the user enters their E2EE passcode).
        if (allDecrypted && !isRecentChunk(chunkStart)) {
          let key = createChunkKey(identity, chunkStart)
          let now = AWSClockSkewHandler.getTime()
          await store.setItem(key, {
            events: events,
            ttl: computeTTL(chunkStart, now),
            created: now
          })
        }
      }
    }

    // 4. Assemble, filter, and deduplicate
    let allEvents = []
    for (let cs of allChunkStarts) {
      let events = cachedChunks[cs]
      if (events) {
        for (let event of events) {
          if (event.timestamp >= until && event.timestamp <= current) {
            allEvents.push(event)
          }
        }
      }
    }

    // Deduplicate by key
    let seen = new Set()
    let uniqueEvents = []
    for (let event of allEvents) {
      if (!seen.has(event.key)) {
        seen.add(event.key)
        uniqueEvents.push(event)
      }
    }

    return uniqueEvents
  }

  async cleanUp() {
    let keys = await store.keys()
    for (let key of keys) {
      // Remove old-format keys (migration from previous cache format)
      if (key.endsWith('.eci')) {
        await store.removeItem(key)
        continue
      }

      // Remove expired chunk entries
      if (key.includes('.chunk.')) {
        let entry = await store.getItem(key)
        if (entry) {
          // Backfill ttl for entries cached before dynamic TTL
          if (entry.ttl == null) {
            let chunkStart = parseInt(key.split('.chunk.')[1])
            entry.ttl = computeTTL(chunkStart, entry.created)
          }
          if (isChunkExpired(entry)) {
            await store.removeItem(key)
          }
        }
      }
    }
  }

  async clearCache() {
    await store.clear()
  }
}

export default (new EventCache())
